import { find } from "lodash";
import template from "./time.html";
import "./time.less";
import {
	EVENT_CLEAR_VALUE,
	EVENT_REVERT_VALUES,
	EVENT_SET_DEFAULT_VALUE,
	EVENT_SET_FORM_PRISTINE,
	EVENT_SET_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_VALUE_CHANGED
} from "constants.js";
import {
	buildTimeValue,
	normalizeTimeValue,
	parseTimeValue
} from "../../../../utils/timeValues";
import {
	DEFAULT_PERIOD,
	MINUTES_PER_DAY,
	TIME_CHOICES,
	TIME_FUSE_OPTIONS,
	TIME_SEARCH_FIELDS,
	buildTimePickerChoices,
	buildTimeSelection,
	getClosestTimeChoiceValue,
	getTimeChoiceDisplay,
	getTimeChoiceTextDisplay,
	toggleTimePeriodValue
} from "./timeChoices";
import { DateTimeFieldAdapter } from "../../../../services/activity-trigger";

export class Controller {
	selection = { period: DEFAULT_PERIOD };
	timeChoices = TIME_CHOICES;
	timeFuseOptions = TIME_FUSE_OPTIONS;
	timeRenderChoiceLimit = MINUTES_PER_DAY;
	timeSearchFields = TIME_SEARCH_FIELDS;
	timeSearchResultLimit = MINUTES_PER_DAY;

	constructor($scope, eventBus, $logger) {
		this._scope = $scope;
		this.$logger = $logger;
	}

	$onInit() {
		this.configureTimeChoices();
		this.capabilities = {
			activityTrigger: new DateTimeFieldAdapter(
				() => this.hasTimeValue(),
				value => {
					this.setValue({ Time: value });
					this.handleValueChanged();
				}
			)
		};
		this._eventBus = this.reportScope.events;
		this._unregister = this.reportFormCtrl.registerFieldController(this);
		this.initialValue = find(this.values, fieldValue => fieldValue.FieldId === this.field.Id);
		this.initialTime = this.normalizeStoredTime(this.initialValue);
		this.timeFieldId = `${this.field.Id}-time`;
		this.setValue(this.initialValue);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (event, params) => {
			if(!this.cleared && this.hasTimeValue() && params?.isInitializing !== true) {
				this.cleared = {
					selection: { ...this.selection }
				};
			}

			this.selection = {
				period: this.selection.period || DEFAULT_PERIOD,
				timeValue: undefined
			};
			this.syncTimeBoxValue();
			this.handleValueChanged();
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!!this.cleared) {
				this.selection = this.cleared.selection;
				this.cleared = undefined;
				this.syncTimeBoxValue();
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (event, params) => {
			this.setValue(params?.value?.Time ? params.value : { Time: params?.value });
			this.handleValueChanged();
		}, this._scope);

		this._eventBus.register(EVENT_SET_DEFAULT_VALUE(this.field.Id), () => {
			this.setValue({ Time: new Date() });
			this.handleValueChanged();
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
	}

	getCurrentValue(isApiCall = false) {
		let result = this.buildResult();

		if(!isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true) {
			if(!result) {
				result = { FieldId: this.field.Id };
				result.hasResultForRuleRun = false;
			}

			result.isDirty = this.isDirty(result);
		}

		return result ? [result] : [];
	}

	buildResult() {
		const timeParts = parseTimeValue(this.selection.timeValue);

		if(!timeParts) {
			return undefined;
		}

		let result = {
			FieldId: this.field.Id,
			Time: buildTimeValue({
				hour: timeParts.hour,
				minute: timeParts.minute,
				second: 0
			}),
			hasFullResult: true
		};

		if(this.repeatId) {
			result = {
				...result,
				repeatId: this.repeatId
			};
		}

		return result;
	}

	setValue(value) {
		const timeValue = this.normalizeStoredTime(value ?? find(this.values, fieldValue => fieldValue.FieldId === this.field.Id));
		this.selection = buildTimeSelection(timeValue, this.selection.period || DEFAULT_PERIOD);
		this.syncTimeBoxValue();
	}

	revertValue() {
		this.setValue({ Time: this.initialTime });
	}

	setPristine() {
		const result = this.buildResult();
		this.initialValue = result;
		this.initialTime = result?.Time;
	}

	configureTimeChoices() {
		const timeChoiceConfiguration = buildTimePickerChoices(this.field.IncrementingStep);

		this.timeChoices = timeChoiceConfiguration.choices;
		this.availableTimeChoices = timeChoiceConfiguration.availableChoices;
		this.timeRenderChoiceLimit = this.availableTimeChoices.length;
	}

	getTimeChoiceToFocus() {
		return getClosestTimeChoiceValue(
			this.selection.timeValue || new Date(),
			this.availableTimeChoices
		);
	}

	setTimeBox(box) {
		this.timeBox = box;
		this.timeBox.setChoices(this.timeChoices, "value", "label", true);
		this.syncTimeBoxValue();
	}

	setTimeDisplay(value) {
		return getTimeChoiceDisplay(value);
	}

	setTimeTextDisplay(value) {
		return getTimeChoiceTextDisplay(value);
	}

	handleTimeChanged(value) {
		const selectedValue = Array.isArray(value) ? value[0] : value;
		this.selection = buildTimeSelection(selectedValue, this.selection.period || DEFAULT_PERIOD);
		this.handleValueChanged();
	}

	togglePeriod() {
		if(this.field.isReadonly || this.field.isDisabled) {
			return;
		}

		const toggledValue = toggleTimePeriodValue(this.selection.timeValue);

		if(!toggledValue) {
			return;
		}

		this.selection = buildTimeSelection(toggledValue, this.selection.period || DEFAULT_PERIOD);
		this.syncTimeBoxValue();
		this.handleValueChanged();
	}

	syncTimeBoxValue() {
		if(!this.timeBox) {
			return;
		}

		this.timeBox.clearSelected();

		if(this.selection.timeValue) {
			this.timeBox.setValueByChoice(this.selection.timeValue);
		}
	}

	getPeriodLabel() {
		return this.selection.period || DEFAULT_PERIOD;
	}

	hasTimeValue() {
		return !!this.buildResult();
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	handleValueChanged() {
		const result = this.buildResult();
		const isDirty = (this.field.visible !== false || this.initialValue !== undefined) && this.isDirty(result);
		this.setFieldDirty(this.field.Id, isDirty);

		this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id, this.repeatId), {
			field: this.field,
			value: result?.Time
		});
	}

	isDirty(result) {
		return this.field.IsNotReported !== true
			&& this.field.IsHidden !== true
			&& ((result?.Time) !== this.initialTime);
	}

	getStoredTimeValue(fieldValue) {
		return fieldValue?.Time ?? fieldValue?.Date;
	}

	normalizeStoredTime(fieldValue) {
		return normalizeTimeValue(this.getStoredTimeValue(fieldValue));
	}

}

export default angular
	.module("vesl.components.report-form.time-field", [])
	.component("timeField", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			repeatId: "<",
			pivotRow: "=",
			pivotColumn: "=",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
